export interface TimeSlot {
  time: string;
  isAvailable: boolean;
  tableNumber?: number;
}

export interface BookedSlot {
  time: string;
  userId: string;
  tableNumber: number;
}

export class MeetingService {
  // B2B slots: 13:30 – 15:30, 15-min steps
  // → [13:30, 13:45, 14:00, …, 15:15]
  static generateAllTimeSlots(): string[] {
    const slots: string[] = [];
    const startMinutes = 13 * 60 + 30;
    const endMinutes = 15 * 60 + 30;
    for (let m = startMinutes; m < endMinutes; m += 15) {
      const hour = Math.floor(m / 60);
      const minute = m % 60;
      slots.push(`${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`);
    }
    return slots;
  }

  // Check if user is busy at specific time
  static async isUserBusy(userId: string, timeSlot: string): Promise<boolean> {
    const { supabase } = await import('../lib/supabase');
    const { data: meetings } = await supabase
      .from('meetings')
      .select('id')
      .eq('meeting_date', '2026-10-06')
      .eq('time_slot', timeSlot)
      .in('status', ['pending', 'confirmed'])
      .or(`requester_id.eq.${userId},recipient_id.eq.${userId}`);
    
    return meetings && meetings.length > 0;
  }

  // Check if table is occupied at specific time
  static async isTableOccupied(tableNumber: number, timeSlot: string): Promise<boolean> {
    const { supabase } = await import('../lib/supabase');
    const { data: meetings } = await supabase
      .from('meetings')
      .select('id')
      .eq('meeting_date', '2026-10-06')
      .eq('time_slot', timeSlot)
      .eq('table_number', tableNumber)
      .in('status', ['pending', 'confirmed']);
    
    return meetings && meetings.length > 0;
  }

  // Find next available table (1-30) - FIXED VERSION
  static async findNextAvailableTable(timeSlot: string, meetingDate: string = '2026-10-06'): Promise<number | null> {
    const { supabase } = await import('../lib/supabase');
    
    // Get all occupied tables for this time slot in ONE query
    const { data: occupiedMeetings, error } = await supabase
      .from('meetings')
      .select('table_number')
      .eq('meeting_date', meetingDate)
      .eq('time_slot', timeSlot)
      .in('status', ['pending', 'confirmed']);

    if (error) {
      console.error('Error fetching occupied tables:', error);
      return null;
    }

    const occupiedTables = occupiedMeetings?.map(m => m.table_number) || [];

    // Find first available table (1-30)
    for (let tableNum = 1; tableNum <= 30; tableNum++) {
      if (!occupiedTables.includes(tableNum)) {
        return tableNum;
      }
    }
    
    return null; // All tables occupied
  }

  // Get available time slots for two users - OPTIMIZED VERSION
  static async getAvailableTimeSlots(
    requesterId: string, 
    recipientId: string, 
    date: string = '2026-10-06'
  ): Promise<TimeSlot[]> {
    try {
      const { supabase } = await import('../lib/supabase');
      
      // Step 1: Generate time slots
      const allTimeSlots = this.generateAllTimeSlots();
      
      // Get meetings where either user is involved (for user conflicts) - this works with RLS
      const { data: userMeetings, error: userError } = await supabase
        .from('meetings')
        .select('time_slot, table_number, requester_id, recipient_id, status, meeting_date, id')
        .eq('meeting_date', date)
        .in('status', ['pending', 'confirmed'])
        .or(`requester_id.eq.${requesterId},recipient_id.eq.${requesterId},requester_id.eq.${recipientId},recipient_id.eq.${recipientId}`)
        .order('created_at', { ascending: true });

      // Get all meetings for the date for table assignment
      const { data: allMeetings, error: adminError } = await supabase
        .from('meetings')
        .select('*')
        .eq('meeting_date', date)
        .in('status', ['pending', 'confirmed'])
        .order('created_at', { ascending: true });

      let uniqueMeetings;
      if (adminError) {
        // Fallback to user meetings only
        uniqueMeetings = userMeetings || [];
      } else {
        uniqueMeetings = allMeetings || [];
      }

      if (userError) {
        return [];
      }

      // Track user busy slots from the meetings we can see
      const userBusySlots = new Set<string>();
      uniqueMeetings?.forEach(meeting => {
        if (meeting.requester_id === requesterId || meeting.recipient_id === requesterId ||
            meeting.requester_id === recipientId || meeting.recipient_id === recipientId) {
          userBusySlots.add(meeting.time_slot);
        }
      });

      const availableSlots: TimeSlot[] = [];

      for (const timeSlot of allTimeSlots) {
        if (userBusySlots.has(timeSlot)) {
          continue; // Skip this slot
        }

        // Table occupancy comes from the meetings already loaded above.
        // Per-slot queries are paused while the project is under load.
        const occupiedTables = (uniqueMeetings || [])
          .filter(meeting => meeting.time_slot === timeSlot)
          .map(meeting => meeting.table_number);
        const occupiedCount = occupiedTables.length;

        if (occupiedCount >= 30) {
          continue; // Skip this slot
        }

        // Step 4: Find first available table using IN-MEMORY logic
        let availableTable = 1;
        while (occupiedTables.includes(availableTable) && availableTable <= 30) {
          availableTable++;
        }

        if (availableTable > 30) {
          continue; // Skip this slot
        }

        // Step 5: Add to available slots
        availableSlots.push({
          time: timeSlot,
          isAvailable: true,
          tableNumber: availableTable
        });
      }

      return availableSlots;

    } catch (error) {
      console.error('Error in getAvailableTimeSlots:', error);
      return [];
    }
  }

  // Schedule a meeting with automatic table assignment - Using book_meeting RPC function
  static async scheduleMeeting(meetingData: {
    requester_id: string;
    recipient_id: string;
    meeting_date: string;
    time_slot: string;
    subject: string;
    description?: string;
    location?: string;
  }): Promise<{ success: boolean; error?: string; tableNumber?: number }> {
    try {
      const { supabase } = await import('../lib/supabase');
      
      // Step 1: Check if slot is available for both users (user conflicts)
      const { data: userConflicts, error: userError } = await supabase
        .from('meetings')
        .select('*')
        .eq('meeting_date', meetingData.meeting_date)
        .eq('time_slot', meetingData.time_slot)
        .in('status', ['pending', 'confirmed'])
        .or(`requester_id.in.(${meetingData.requester_id},${meetingData.recipient_id}),recipient_id.in.(${meetingData.requester_id},${meetingData.recipient_id})`);

      if (userError) {
        console.error('Error checking user conflicts:', userError);
        return { success: false, error: userError.message };
      }

      if (userConflicts && userConflicts.length > 0) {
        return { 
          success: false, 
          error: "One of the participants already has a meeting scheduled at this time. Please choose a different time slot." 
        };
      }

      // Step 2: Use book_meeting RPC function to handle table assignment and meeting creation
      
      const { data: rpcResult, error: rpcError } = await supabase.rpc('book_meeting', {
        p_requester: meetingData.requester_id,
        p_recipient: meetingData.recipient_id,
        p_date: meetingData.meeting_date,
        p_time: meetingData.time_slot,
        p_subject: meetingData.subject
      });

      if (rpcError) {
        console.error('Error calling book_meeting RPC:', rpcError);
        return { success: false, error: rpcError.message };
      }

      if (!rpcResult || !rpcResult.success) {
        console.error('book_meeting RPC returned failure:', rpcResult);
        return { success: false, error: rpcResult?.message || 'Failed to book meeting' };
      }

      const assignedTable = rpcResult.table_number;

      // Step 3: Update the meeting with additional details (description, location) if needed
      if (meetingData.description || meetingData.location) {
        const { error: updateError } = await supabase
          .from('meetings')
          .update({
            description: meetingData.description || null,
            location: meetingData.location || null
          })
          .eq('requester_id', meetingData.requester_id)
          .eq('recipient_id', meetingData.recipient_id)
          .eq('meeting_date', meetingData.meeting_date)
          .eq('time_slot', meetingData.time_slot)
          .eq('table_number', assignedTable);

        if (updateError) {
          console.error('Error updating meeting details:', updateError);
          // Don't return error here, meeting was created successfully
        }
      }

      return { success: true, tableNumber: assignedTable };
    } catch (error) {
      console.error('Error in scheduleMeeting:', error);
      return { success: false, error: 'Failed to schedule meeting' };
    }
  }

  // Get user meetings - OPTIMIZED VERSION
  static async getUserMeetings(userId: string): Promise<any[]> {
    try {
      const { supabase } = await import('../lib/supabase');
      
      // OPTIMIZATION: Simplified query with only essential fields
      const { data, error } = await supabase
        .from('meetings')
        .select(`
          id,
          requester_id,
          recipient_id,
          meeting_date,
          time_slot,
          table_number,
          subject,
          description,
          location,
          status,
          created_at,
          requester:users!meetings_requester_id_fkey(name, company, avatar_url),
          recipient:users!meetings_recipient_id_fkey(name, company, avatar_url)
        `)
        .or(`requester_id.eq.${userId},recipient_id.eq.${userId}`)
        .eq('meeting_date', '2026-10-06')
        .order('time_slot', { ascending: true });

      if (error) {
        console.error('Error fetching meetings:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getUserMeetings:', error);
      return [];
    }
  }

  // Delete meeting
  static async deleteMeeting(meetingId: string, currentUserId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { supabase } = await import('../lib/supabase');
      
      const { error } = await supabase
        .from('meetings')
        .delete()
        .eq('id', meetingId)
        .or(`requester_id.eq.${currentUserId},recipient_id.eq.${currentUserId}`);

      if (error) {
        console.error('Error deleting meeting:', error);
        return { success: false, error: error.message };
      }
      return { success: true };
    } catch (error) {
      console.error('Error in deleteMeeting:', error);
      return { success: false, error: 'Failed to delete meeting' };
    }
  }

  // Accept meeting
  static async acceptMeeting(meetingId: string, userId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { supabase } = await import('../lib/supabase');
      
      const { error } = await supabase
        .from('meetings')
        .update({ status: 'confirmed' })
        .eq('id', meetingId)
        .eq('recipient_id', userId);

      if (error) {
        console.error('Error accepting meeting:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Error in acceptMeeting:', error);
      return { success: false, error: 'Failed to accept meeting' };
    }
  }

  // Reject meeting
  static async rejectMeeting(meetingId: string, userId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { supabase } = await import('../lib/supabase');
      
      const { error } = await supabase
        .from('meetings')
        .delete()
        .eq('id', meetingId)
        .eq('recipient_id', userId);

      if (error) {
        console.error('Error rejecting meeting:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Error in rejectMeeting:', error);
      return { success: false, error: 'Failed to reject meeting' };
    }
  }

  // Clear all data (admin function)
  static async clearAllData(): Promise<{ success: boolean; error?: string }> {
    try {
      const { supabase } = await import('../lib/supabase');
      
      // Delete all meetings
      await supabase.from('meetings').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      
      // Delete all connections
      await supabase.from('connections').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      
      // Delete all users
      await supabase.from('users').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      
      return { success: true };
    } catch (error) {
      console.error('Error clearing data:', error);
      return { success: false, error: 'Failed to clear data' };
    }
  }

  // Clean up duplicate meetings (admin function)
  static async cleanupDuplicateMeetings(): Promise<{ success: boolean; error?: string; removedCount?: number }> {
    try {
      const { supabase } = await import('../lib/supabase');
      
      // Get all meetings
      const { data: allMeetings, error: fetchError } = await supabase
        .from('meetings')
        .select('*')
        .eq('meeting_date', '2026-10-06')
        .in('status', ['pending', 'confirmed'])
        .order('created_at', { ascending: true });

      if (fetchError) {
        console.error('Error fetching meetings:', fetchError);
        return { success: false, error: fetchError.message };
      }

      if (!allMeetings || allMeetings.length === 0) {
        return { success: true, removedCount: 0 };
      }

      // Group meetings by time slot and table
      const meetingsBySlot = new Map<string, any[]>();
      
      allMeetings.forEach(meeting => {
        const key = `${meeting.time_slot}-${meeting.table_number}`;
        if (!meetingsBySlot.has(key)) {
          meetingsBySlot.set(key, []);
        }
        meetingsBySlot.get(key)!.push(meeting);
      });

      let removedCount = 0;
      const meetingsToDelete: string[] = [];

      // Find duplicates and mark the newer ones for deletion
      meetingsBySlot.forEach((meetings, key) => {
        if (meetings.length > 1) {
          // Keep the first (oldest) meeting, delete the rest
          const toKeep = meetings[0];
          const toDelete = meetings.slice(1);
          
          toDelete.forEach(meeting => {
            meetingsToDelete.push(meeting.id);
            removedCount++;
          });
        }
      });

      // Delete duplicate meetings
      if (meetingsToDelete.length > 0) {
        const { error: deleteError } = await supabase
          .from('meetings')
          .delete()
          .in('id', meetingsToDelete);

        if (deleteError) {
          console.error('Error deleting duplicate meetings:', deleteError);
          return { success: false, error: deleteError.message };
        }
      }

      return { success: true, removedCount };
    } catch (error) {
      console.error('Error cleaning up duplicates:', error);
      return { success: false, error: 'Failed to clean up duplicate meetings' };
    }
  }
  // Update meeting status (accept/reject)
  static async updateMeetingStatus(
    meetingId: string, 
    status: 'confirmed' | 'rejected' | 'cancelled'
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { supabase } = await import('../lib/supabase');
      
      const { error } = await supabase
        .from('meetings')
        .update({ status })
        .eq('id', meetingId);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: 'Failed to update meeting status' };
    }
  }

  // Get pending meeting requests for user
  static async getPendingMeetingRequests(userId: string): Promise<any[]> {
    try {
      const { supabase } = await import('../lib/supabase');
      
      const { data, error } = await supabase
        .from('meetings')
        .select(`
          *,
          requester:users!meetings_requester_id_fkey(*),
          recipient:users!meetings_recipient_id_fkey(*)
        `)
        .eq('recipient_id', userId)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching pending requests:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching pending requests:', error);
      return [];
    }
  }
}
